import { CheckboxGroupOption, H3, H4, H5, H6, Id, TernaryCheckboxControl } from "@dgs/core";
import { useField } from "formik";
import { FC, ReactNode, useCallback } from "react";
import styled from "styled-components";
import { Preference } from "../../shared/types";

const Indentation = styled.div`
	display: grid;
	padding-left: ${({ theme }) => theme.spacing(6)};
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: repeat(2, 1fr);
	}

	${({ theme }) => theme.breakpoints.desktop} {
		grid-template-columns: repeat(4, 1fr);
	}
`;

const ChildWrapper = styled.div`
	display: grid;
	grid-auto-rows: max-content;
	grid-row-gap: ${({ theme }) => theme.spacing(2)};
	padding-left: ${({ theme }) => theme.spacing(6)};
`;

interface CategoryProps {
	index: number;
	preference: Preference;
	calledRecursively?: boolean;
}

export const CategoryOrPreferences: FC<CategoryProps> = ({ index, preference, calledRecursively }) => {
	if (preference.children.length > 0) {
		return (
			<div>
				{preference.children.length > 1 && preference.children[0].children.length === 0 ? (
					<AllCheckBox
						label={<Category index={index} title={preference.label} />}
						preferences={preference.children}
						name={`all-${preference.id}`}
					/>
				) : (
					<Category index={index} title={preference.label} />
				)}
				{calledRecursively ? (
					<ChildWrapper>
						{preference.children.map((p) => {
							const idx = index + 1;
							return <CategoryOrPreferences key={p.id} index={idx} preference={p} calledRecursively={true} />;
						})}
					</ChildWrapper>
				) : (
					<Indentation>
						{preference.children.map((p) => {
							const idx = index + 1;
							return <CategoryOrPreferences key={p.id} index={idx} preference={p} calledRecursively={true} />;
						})}
					</Indentation>
				)}
			</div>
		);
	}

	if (calledRecursively) {
		return <CheckboxGroupOption label={preference.label} value={preference.id} />;
	}

	return <Category index={3} title={preference.label} />;
};

const Category: FC<{ index: number; title: string }> = ({ index, title }) => {
	switch (index) {
		case 3:
			return <H3>{title}</H3>;
		case 4:
			return <H4>{title}</H4>;
		case 5:
			return <H5>{title}</H5>;
		default:
			return <H6>{title}</H6>;
	}
};

const AllCheckBox: FC<{ label: ReactNode; preferences: Preference[]; name: string }> = ({
	label,
	preferences,
	name,
}) => {
	const [field, , helpers] = useField<Id[]>("preferenceIds");

	const handleAllSelect = useCallback(
		(checked: boolean) => {
			if (checked) {
				helpers.setValue([...new Set([...field.value, ...preferences.map((preference) => preference.id)])]);
			} else {
				helpers.setValue(field.value.filter((id) => !preferences.find((preference) => preference.id === id)));
			}
		},
		[field.value, helpers, preferences],
	);

	const getCheckboxState = useCallback(() => {
		if (preferences.every((preference) => field.value.includes(preference.id))) {
			return "TRUE";
		}

		if (preferences.some((preference) => field.value.includes(preference.id))) {
			return "PARTIAL";
		}

		return "FALSE";
	}, [field.value, preferences]);

	return (
		<TernaryCheckboxControl
			label={label}
			name={name}
			value={getCheckboxState()}
			onChange={(e) => handleAllSelect(e.target.value)}
		/>
	);
};
